import React, {useEffect, useRef, useState} from 'react';
import AnimatedLink from "../../ui/motion/AnimatedLink";
import FaqList from "../../ui/faq/FaqList";

interface Section {
    id: string;
    title: string;
}

export default function Parents() {
    const [activeSection, setActiveSection] = useState<string>('intro');
    const contentRef = useRef<HTMLDivElement>(null);
    const [hasScrolled, setHasScrolled] = useState<boolean>(true);

    // Define sections for the table of contents
    const sections: Section[] = [
        {id: 'intro', title: 'Wprowadzenie'},
        {id: 'course-structure', title: 'Struktura kursu'},
        {id: 'support', title: 'Wsparcie psychiczne'},
        {id: 'approach', title: 'Podejście do kursu'},
        {id: 'expectations', title: 'Czego się spodziewać'},
        {id: 'potential-problems', title: 'Potencjalne problemy'}
    ];

    const faqItems = [
        {
            question: "Dziecko nie chce przerabiać Kursu",
            answer: "Nastolatkowie mają to do siebie, że jeżeli coś im się każe zrobić, to wolą zrobić po swojemu. Dlatego znacznie lepiej sprawdzi się inspirowanie. Powiedz, że od dzisiaj ma dostęp do Kursu. Powiedz, czego może się nauczyć. Powiedz, co może z tego zyskać. A następnie, daj swojemu dziecku czas na oswojenie się z tą informacją."
        },
        {
            question: "Dziecko potrzebuje pomocy z materiałem",
            answer: "W takim przypadku najlepiej sprawdzi się wzięcie udziału w spotkaniu z Twórcą Kursu lub zadanie pytania na naszej społeczności. To, jak wziąć udział w spotkaniu lub dołączyć do społeczności, pokazujemy w tygodniu wprowadzającym.\n\nJeżeli największą blokadą dziecka jest obawa przed oceną innych, może też wysłać do nas wiadomość na adres kontakt@produkacja.pl\n\nW kursie pokazujemy, w jaki sposób pracować nad swoją pewnością siebie i jak przezwyciężyć takie blokady, jednak dla nowych kursantów taka opcja może być pomocna. Najważniejsze to pamiętać, że nie ma głupich pytań i proszenie o pomoc nie jest oznaką słabości, tylko raczej dojrzałości."
        },
        {
            question: "Dziecko nie przerabia Kursu systematycznie",
            answer: "Systematyczności uczymy bezpośrednio w Kursie - i po przerobieniu pierwszych tygodni ten problem zniknie raz na zawsze. Jednak jeżeli dziecko jest na samym początku Kursu i nie za bardzo wie, jak do tego podejść, jako rodzic możesz mu w tym pomóc. Wystarczy stworzyć prosty plan - np. poniedziałek 45min, środa 45min, czwartek 45min, sobota 45min. I pomóc uczniowi się zabrać do działania."
        }
    ];

    // Update active section when scrolling
    useEffect(() => {
        const handleScroll = () => {
            if (!hasScrolled || !contentRef.current) return;

            // Find which section is currently in view
            let foundActive = false;
            for (let i = sections.length - 1; i >= 0; i--) {
                const section = sections[i];
                const element = document.getElementById(section.id);
                if (element) {
                    const rect = element.getBoundingClientRect();
                    if (rect.top <= 150) {
                        setActiveSection(section.id);
                        foundActive = true;
                        break;
                    }
                }
            }

            // If no section is in view, default to the first section
            if (!foundActive && sections.length > 0) {
                setActiveSection(sections[0].id);
            }
        };

        window.addEventListener('scroll', handleScroll);
        handleScroll(); // Initialize

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [sections, hasScrolled]);

    const scrollToSection = (sectionId: string) => {
        const element = document.getElementById(sectionId);
        if (element) {
            setHasScrolled(false);
            setActiveSection(sectionId);

            window.scrollTo({
                top: element.offsetTop - 20,
                behavior: 'smooth'
            });

            setTimeout(() => {
                setHasScrolled(true);
            }, 1000);
        }
    };

    return (
        <div className="row container">
            {/* Table of contents - left side */}
            <div className="col-lg-3 d-none d-lg-block">
                <div className="sticky-top" style={{
                    top: '100px',
                    padding: '1.5rem',
                    backgroundColor: 'rgba(33, 37, 41, 0.3)',
                    borderRadius: '8px',
                    boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)',
                    marginTop: '2rem',
                }}>
                    <h5 className="mb-3 fw-bold text-light">Spis treści</h5>
                    <div className="list-group">
                        {sections.map((section) => (
                            <AnimatedLink
                                key={section.id}
                                to={`#${section.id}`}
                                className={`list-group-item list-group-item-action border-0 ${
                                    activeSection === section.id ? 'active bg-primary bg-opacity-25 text-white fw-semibold rounded' : 'bg-transparent text-light'
                                }`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    scrollToSection(section.id);
                                }}
                                style={{cursor: 'pointer'}}
                            >
                                {section.title}
                            </AnimatedLink>
                        ))}
                    </div>
                </div>
            </div>

            {/* Main content - right side */}
            <div className="col-lg-9 ps-lg-4" ref={contentRef}>
                <div className="content pt-5">
                    <section id="intro" className="mb-8">
                        <h2 className="mb-4 fw-bold text-light">Wprowadzenie</h2>
                        <p className="lead text-light">Cześć,</p>
                        <p className="text-light">
                            Bardzo się cieszę, że jesteś na tej stronie. To bardzo dobrze o Tobie świadczy. Poświęć 3
                            minuty na przeczytanie
                            poniższej treści, aby dowiedzieć się, w jaki sposób możesz wspierać swoje dziecko w Kursie.
                        </p>
                    </section>

                    <section id="course-structure" className="mb-8">
                        <h3 className="mb-3 fw-bold text-light">Struktura kursu</h3>
                        <div className="card bg-dark border-light mb-4 shadow-sm">
                            <div className="card-body">
                                <p className="text-light">
                                    Kurs trwa 9 tygodni, podczas których Twoje dziecko nauczy się wszystkiego na temat
                                    Efektywnego Działania.
                                    Idealnie będzie poświęcać na Kurs około 2-3 godziny w tygodniu: ~1-1.5h na
                                    obejrzenie filmów oraz drugie
                                    tyle na wykonanie ćwiczeń.
                                </p>
                                <p className="text-light">
                                    Każdy uczestnik Kursu otrzymuje dostęp do naszej prywatnej społeczności oraz do
                                    cotygodniowych, grupowych spotkań
                                    z Twórcą Kursu, gdzie każdy uczeń może uzyskać odpowiedzi na swoje pytania.
                                </p>
                                <p className='mb-0 text-light'>
                                    Aby zobaczyć pełną strukturę Kursu, wraz z opisem każdego tygodnia oraz każdej
                                    lekcji, <AnimatedLink className='text-success text-decoration-underline'
                                                          openLinkInNewTab={true}
                                                          to='https://docs.google.com/document/d/17eQ5cB8ZD7NsbcsHteTOztJVvydv4rPBPXL2dW2XeRI/edit?usp=sharing'>kliknij
                                    tutaj</AnimatedLink>.
                                </p>
                            </div>
                        </div>
                        <p className="text-light">
                            Proces może wymagać wielu zmian i eksperymentów. Najważniejsze to podchodzić do Kursu z
                            otwartym umysłem
                            i po prostu próbować. A jeżeli coś nie idzie, to nie poddawać się, tylko działać dalej. I
                            tego też uczymy w Kursie.
                        </p>
                    </section>

                    <section id="support" className="mb-8">
                        <h3 className="mb-3 fw-bold text-light">Wsparcie psychiczne</h3>
                        <div className="card bg-dark border-primary mb-4 shadow-sm">
                            <div className="card-body">
                                <p className="text-light">
                                    Jako rodzic, bardzo ważne jest to, aby wspierać swoje dziecko psychicznie. Czyli w
                                    momencie, gdy dziecko będzie
                                    wprowadzało w życie pewne zmiany, będzie próbowało nowych rzeczy, będzie robiło coś
                                    dla niego niekomfortowego,
                                    to bądź dla niego wsparciem.
                                </p>
                                <p className="mb-0 text-light">
                                    Przytul je. Powiedz, że sobie poradzi. Powiedz, że to jest w porządku, że to jest
                                    proces.
                                </p>
                            </div>
                        </div>
                        <div className="bg-dark bg-opacity-50 border-0 text-center shadow-sm p-3 rounded">
                            <p className="fst-italic mb-0 text-light fs-10">
                                "Sukces to przechodzenie od porażki do porażki bez utraty entuzjazmu" ~Winston Churchill
                            </p>
                        </div>
                        <p className="mt-3 text-light">
                            Jeżeli dziecko będzie miało problem z jakimś ćwiczeniem lub ze zrozumieniem zagadnień z
                            Kursu, najlepiej będzie
                            zachęcić je do wzięcia udziału w jednym z naszych spotkań lub do napisania wiadomości na
                            naszej społeczności.
                            Wielu nastolatków jest dość nieśmiałych i wydaje im się, że "nie wypada" prosić o pomoc. Nic
                            bardziej mylnego!
                            Nie ma głupich pytań. I na Produkacji tworzymy bezpieczną przestrzeń - tak, aby każdy uczeń
                            mógł czuć się tam komfortowo.
                        </p>
                    </section>

                    <section id="approach" className="mb-8">
                        <h3 className="mb-3 fw-bold text-light">Podejście do kursu</h3>
                        <div className="alert bg-warning bg-opacity-100 text-dark">
                            <p className='mb-0 fw-medium'>
                                <strong>Bardzo istotne jest</strong> to, aby jako rodzic nie naciskać i nie przymuszać
                                dziecka do przerabiania materiałów.
                                Uczeń samodzielnie musi widzieć w tym sens. I właśnie dlatego, dostęp do Kursu jest na
                                CAŁE ŻYCIE. Dlatego nie musisz
                                się martwić tym, że Twoje dziecko może nie chcieć zacząć go przerabiać za 2 dni lub za
                                tydzień.
                            </p>
                        </div>
                        <p className="text-light">
                            Nastolatkowie mają to do siebie, że jeżeli coś im się każe zrobić, to wolą zrobić po
                            swojemu, wolą zrobić na odwrót.
                            Dlatego znacznie lepiej sprawdzi się inspirowanie. Powiedz, że od dzisiaj ma dostęp do
                            Kursu. Powiedz, czego może się
                            nauczyć. Powiedz, co może z tego zyskać. A następnie, daj swojemu dziecku czas na oswojenie
                            się z tą informacją.
                        </p>
                        <div className="card bg-dark bg-opacity-75 mb-4 border-0 shadow-sm">
                            <div className="card-body">
                                <p className="text-light mb-0">
                                    Z naszych statystyk wynika, że około 68% wszystkich kursantów rozpoczęła
                                    przerabianie Kursu przynajmniej tydzień
                                    po jego zakupie, w tym 47% 2 tygodnie później, a 31% przynajmniej miesiąc później.
                                    Jednak ten moment w końcu
                                    następuje - dlatego nie musisz się tym martwić.
                                </p>
                            </div>
                        </div>
                        <p className="text-light">
                            Dziecko nie będzie miało możliwości zapomnienia o Kursie, ponieważ regularnie wysyłamy
                            naszym kursantom informacje o
                            nowościach, o zmianach oraz przypomnienia o cotygodniowych spotkaniach.
                        </p>
                        <p className="text-light">
                            Kurs jest aktualizowany na bieżąco. Jeżeli widzimy, że wielu uczniów pyta lub prosi o tę
                            samą rzecz, dorabiamy lub
                            edytujemy lekcję na ten temat.
                        </p>
                    </section>

                    <section id="expectations" className="mb-8">
                        <h3 className="mb-3 fw-bold text-light">Czego możesz się spodziewać?</h3>
                        <p className="text-light">
                            <AnimatedLink to="/opinie" openLinkInNewTab={true}
                                          className="text-decoration-none fw-bold text-success text-decoration-underline">Zobacz
                                sekcję z
                                opiniami</AnimatedLink>.
                        </p>
                    </section>

                    <section id="potential-problems" className="mb-8">
                        <h3 className="mb-3 fw-bold text-light">Potencjalne problemy:</h3>
                        <FaqList doNotCenter={true} items={faqItems}/>
                        <p className='text-light mt-2'>Jeżeli wciąż masz jakieś pytania, śmiało skontaktuj skontaktuj
                            się z
                            nami pisząc na adres <a href="mailto:kontakt@produkacja.pl"
                                                    className='text-success text-decoration-none'>
                                kontakt@produkacja.pl
                            </a>.</p>
                    </section>
                </div>
            </div>
        </div>
    );
};
